import { Component, Input, OnInit, ViewEncapsulation } from '@angular/core';
import { FormControl } from '@angular/forms';
import { Storage } from '@ionic/storage';
import { GroupChoice } from './group-choice.model';

enum GroupStateEnum {
  NOT_SELECTED,
  FULLY_SELECTED,
  PARTIALLY_SELECTED,
}

/**
 * Allows to use a special select to be able to have options to select as `groups` related to these groups some nested options that are the `subitems`.
 * @example
 *  <angular-select-hierarchical
 *    data-testid="testId"
 *    [control]="myControl"
 *    [groups]="myGroups"
 *    [label]="labels.channels">
 *  </angular-select-hierarchical>
 */
@Component({
  selector: 'app-select-hierarchical',
  templateUrl: './select-hierarchical.component.html',
  styleUrls: ['./select-hierarchical.component.scss'],
  encapsulation: ViewEncapsulation.None,
})
export class SelectHierarchicalComponent implements OnInit {
  @Input() control: FormControl;
  @Input() groups: GroupChoice[];
  @Input() localStorageKey: string;
  @Input() label: string;

  groupStates: { [key: string]: GroupStateEnum } = {};
  selectedSet = new Set();
  GroupStateEnum = GroupStateEnum;

  constructor(private storage: Storage) {}

  async ngOnInit() {
    this.control.value?.forEach(value => this.selectedSet.add(value));
    this.control.valueChanges.subscribe(values => {
      this.selectedSet = new Set(values);
      this.saveToLocalStorage();
    });
    await this.loadFromLocalStorage();
    this.instantiateHierarchy();
  }

  /**
   * Allows to loop on the groups to check if the group is partially or fully selected.
   */
  private instantiateHierarchy() {
    this.groups.forEach(group => {
      group.selected = new Set();
      group.subitems.forEach(item => {
        if (this.selectedSet.has(item.id)) {
          this.computeGroupState(item, group);
        }
      });
    });
  }

  /**
   * Allows to compute on the fly the group state to evaluate if the group is not selected at all or partially or fully selected.
   * @param item the group sub item selected.
   * @param group the group related to the choice.
   */
  computeGroupState(item, group: GroupChoice) {
    if (group.selected.has(item.id)) {
      group.selected.delete(item.id);
    } else {
      group.selected.add(item.id);
    }
    if (group.selected.size > 0) {
      if (group.selected.size < group.subitems.length) {
        this.groupStates[group.label] = GroupStateEnum.PARTIALLY_SELECTED;
      } else {
        this.groupStates[group.label] = GroupStateEnum.FULLY_SELECTED;
      }
    } else {
      this.groupStates[group.label] = GroupStateEnum.NOT_SELECTED;
    }
  }

  /**
   * Allows to toggle the group.
   * If the event is related to a checked event, then select all the items related to the given group.
   * If the event is related to a unchecked event, then unselect all the items related to the given group.
   * @param $event a MatCheckbox change event `{checked: boolean}`.
   * @param group the group that was clicked.
   */
  toggleGroup($event, group: GroupChoice) {
    if (!group.selected) {
      group.selected = new Set();
    }
    const { checked } = $event;
    this.groupStates[group.label] = checked ? GroupStateEnum.FULLY_SELECTED : GroupStateEnum.NOT_SELECTED;
    group.subitems.forEach(item => {
      if (checked) {
        group.selected.add(item.id);
        this.selectedSet.add(item.id);
      } else {
        group.selected.delete(item.id);
        this.selectedSet.delete(item.id);
      }
    });
    this.control.setValue(Array.from(this.selectedSet));
  }

  /**
   * Allows to load a value from the Ionic Storage for the given `localStorageKey`.
   */
  private async loadFromLocalStorage() {
    if (this.localStorageKey) {
      const value = await this.storage.get(this.localStorageKey);
      if (value) {
        this.control.setValue(value);
      }
    }
  }

  /**
   * Allows to save the selected values into the Ionic Storage for the given `localStorageKey`.
   */
  private saveToLocalStorage(values?) {
    if (this.localStorageKey) {
      this.storage.set(this.localStorageKey, values || this.control.value);
    }
  }
}
